import { ID_PATTERN, type DialerSource, type TasksQuery } from "@gioia365/contracts";

// L'indirizzo di «Le cose da fare» (piano M5, Task 18; mock-up `task.html`): la scheda aperta, «Di chi», «Cosa» e l'ordine
// stanno nell'indirizzo, con le parole del mock-up (`/task?vista=scaduti&chi=…`), così un link o il ritorno dalla scheda di un
// Lead riapre la stessa vista. I valori di partenza («Oggi», tutto il team, «Tutto», «Come vanno fatti») non si scrivono.
//
// La scheda di un Lead aperta dai Task porta da dove arriva in `da=task:<vista>:<chi>` (mock-up, `fromHere`): serve soltanto a
// «Torna ai Task», che riapre la stessa scheda e la stessa persona. Non fa partire nessuna chiamata: «Chiama» passa dallo stato
// della cronologia (ruling Q7 e Q57), così ricaricare la pagina o condividere l'indirizzo non richiama.

/** Le quattro schede: la parola dell'indirizzo e la vista del server. */
export const TASK_VIEW_PARAMS = { oggi: "today", scaduti: "overdue", prossimi: "upcoming", completati: "done" } as const satisfies Record<string, TasksQuery["view"]>;
export type TaskViewParam = keyof typeof TASK_VIEW_PARAMS;

// «Cosa»: i richiami («Telefonate»), le disco e le demo (piano M6, R7, Task 6).
const KIND_PARAMS = { telefonate: "calls", disco: "disco", demo: "demo" } as const satisfies Record<string, TasksQuery["kind"]>;
/** Il valore di «Cosa» nell'indirizzo, se è uno di quelli che si scrivono. */
export function kindParamOf(value: string): TasksSearch["cosa"] {
  return has(KIND_PARAMS, value) ? value : undefined;
}
const SORT_PARAMS = { priorita: "priority", lead: "lead", status: "status" } as const satisfies Record<string, TasksQuery["sort"]>;

export type TasksSearch = {
  vista?: TaskViewParam;
  /** Una persona dell'azienda (la sua appartenenza); senza, tutto il team. Vale soltanto per chi vede i Task di tutti. */
  chi?: string;
  cosa?: keyof typeof KIND_PARAMS;
  ordine?: keyof typeof SORT_PARAMS;
};

const has = <T extends object>(map: T, value: unknown): value is keyof T => typeof value === "string" && Object.hasOwn(map, value);

/** Una persona dell'indirizzo: un identificativo possibile, mai «all» (che è già tutto il team). */
function personParam(value: unknown): string | undefined {
  // Un identificativo di sole cifre torna dall'indirizzo come numero (il router legge i valori come JSON).
  const text = typeof value === "number" && Number.isSafeInteger(value) ? String(value) : value;
  return typeof text === "string" && text !== "all" && ID_PATTERN.test(text) ? text : undefined;
}

/** I parametri dell'indirizzo `/task`: un valore sconosciuto o di forma sbagliata vale come assente. */
export function validateTasksSearch(search: Record<string, unknown>): TasksSearch {
  const result: TasksSearch = {};
  if (has(TASK_VIEW_PARAMS, search.vista) && search.vista !== "oggi") result.vista = search.vista;
  const chi = personParam(search.chi);
  if (chi !== undefined) result.chi = chi;
  if (has(KIND_PARAMS, search.cosa)) result.cosa = search.cosa;
  if (has(SORT_PARAMS, search.ordine)) result.ordine = search.ordine;
  return result;
}

/** Che cosa chiedere al server per l'indirizzo: «Di chi» soltanto per chi vede i Task di tutti (agli altri il server lo ignora). */
export type TasksListQuery = Pick<TasksQuery, "view" | "kind" | "sort"> & { who?: string };

export function tasksListQueryOf(search: TasksSearch, canFilterByPerson: boolean): TasksListQuery {
  const query: TasksListQuery = {
    view: TASK_VIEW_PARAMS[search.vista ?? "oggi"],
    kind: search.cosa ? KIND_PARAMS[search.cosa] : "all",
    sort: search.ordine ? SORT_PARAMS[search.ordine] : "due",
  };
  if (canFilterByPerson && search.chi !== undefined) query.who = search.chi;
  return query;
}

/** Il parametro `da` della scheda di un Lead aperta da questa vista e da questa persona. */
export function taskSourceParam(search: TasksSearch): string {
  return `task:${search.vista ?? "oggi"}:${search.chi ?? "all"}`;
}

const SOURCE = /^task:(oggi|scaduti|prossimi|completati):([A-Za-z0-9_-]{1,128})$/;

/** Da dove arriva la scheda di un Lead (`da=task:<vista>:<chi>`): l'indirizzo dei Task a cui torna, oppure `null`. */
export function tasksSearchFromSource(value: unknown): TasksSearch | null {
  if (typeof value !== "string") return null;
  const match = SOURCE.exec(value);
  if (!match?.[1] || !match[2]) return null;
  const vista = match[1] as TaskViewParam;
  const chi = personParam(match[2]);
  return { ...(vista === "oggi" ? {} : { vista }), ...(chi === undefined ? {} : { chi }) };
}

/** `da` della scheda aperta dal dettaglio della pagina «Opportunità» (mock-up: `?opportunita=1&da=opportunita`). */
export const OPPORTUNITIES_SOURCE = "opportunita";

/** I parametri della scheda di un Lead (`/lead/$leadId`). */
export type LeadCardSearch = {
  /** Da dove arriva: una vista dei Task (`task:<vista>:<chi>`, per «Torna ai Task») o la pagina «Opportunità». */
  da?: string;
  /** `1`: la scheda apre una volta la finestra «Opportunità di …» (LEAD-2), poi lo toglie dall'indirizzo. */
  opportunita?: 1;
  /**
   * L'elemento della Timeline da portare in vista una volta all'arrivo, `<tipo>:<id>` (`call`, `note`, `change`, `event`, `whatsapp`):
   * il link di una riga di «Attività» (SEZ-8). Poi esce dall'indirizzo, come `opportunita`.
   */
  evento?: string;
};

/** I tipi di elemento della Timeline che `evento` può nominare. */
const TIMELINE_ITEM_KINDS = ["call", "note", "change", "event", "whatsapp"] as const;
const TIMELINE_ITEM_ID = /^[A-Za-z0-9_.:@-]{1,128}$/;

/** L'elemento della Timeline di `evento` (`<tipo>:<id>`), oppure `null` se non ha la forma giusta. */
export function timelineItemOfParam(value: unknown): { kind: (typeof TIMELINE_ITEM_KINDS)[number]; id: string } | null {
  if (typeof value !== "string") return null;
  const separator = value.indexOf(":");
  const kind = TIMELINE_ITEM_KINDS.find((candidate) => candidate === value.slice(0, separator));
  const id = value.slice(separator + 1);
  return separator > 0 && kind !== undefined && TIMELINE_ITEM_ID.test(id) ? { kind, id } : null;
}

/** I parametri della scheda di un Lead: `da` quando dice una vista dei Task o la pagina «Opportunità», `opportunita=1`. */
export function validateLeadCardSearch(search: Record<string, unknown>): LeadCardSearch {
  const result: LeadCardSearch = {};
  if (typeof search.da === "string" && (search.da === OPPORTUNITIES_SOURCE || tasksSearchFromSource(search.da) !== null)) result.da = search.da;
  // Il router legge i valori come JSON: `1` arriva come numero.
  if (search.opportunita === 1 || search.opportunita === "1") result.opportunita = 1;
  if (timelineItemOfParam(search.evento) !== null) result.evento = search.evento as string;
  return result;
}

/**
 * L'indirizzo dei Task della cascata dai Task (piano M5, Task 21): la stessa scheda e la stessa persona della sessione, per
 * «Torna ai Task» del riepilogo e della scheda del Lead.
 */
export function tasksSearchOfDialer(source: Extract<DialerSource, { type: "task_view" }>): TasksSearch {
  return { ...(source.view === "overdue" ? { vista: "scaduti" as const } : {}), ...(source.who === "all" ? {} : { chi: source.who }) };
}

/** I parametri della scheda del Lead aperto da una sessione del Power Dialer: dai Task, da dove arriva (`da`); da una lista, niente. */
export function leadCardSearchOf(source: DialerSource): { da?: string } {
  return source.type === "task_view" ? { da: taskSourceParam(tasksSearchOfDialer(source)) } : {};
}
